import {Component, h} from 'preact'
import ButtonControl from '../../button-control'
import Icon from '../../assets'
import SearchBox from '../../search-box/SearchBox'
import PillContainer from '../../pills/PillContainer'
import Pill from '../../pills/Pill'
import {Filters, FilterSelector} from '../index'
import FiltersMenu from "./FiltersMenu";
import formatDateInLocalTimezone from "../../utils/dateFormatting";

type Props = {
  filters: Array<FilterSelector>,
  appliedFilters?: Filters,
  onFilterChange: (filter: string, payload: Array<string> | string | null) => void,
  onClearFilterCriteria: () => void,
  withSearch?: boolean,
  searchPlaceholder?: string,
}

type State = {
  areFiltersOpen: boolean,
  areFiltersOpenFromFilterPill: boolean,
  areFiltersOpenFromMain: boolean,
  areAppliedFiltersVisible: boolean,
  selectedFilter: string | undefined,
  filters: Array<FilterSelector>
}

class OverviewFilters extends Component<Props, State> {
  wrapperRef: null | HTMLElement = null
  constructor (props: Props) {
    super(props)

    this.state = {
      areFiltersOpen: false,
      areFiltersOpenFromFilterPill: false,
      areFiltersOpenFromMain: false,
      areAppliedFiltersVisible: true,
      selectedFilter: undefined,
      filters: props.filters
    }
  }

  addHandler () {
    const { areFiltersOpen, areFiltersOpenFromFilterPill, areFiltersOpenFromMain } = this.state
    if(areFiltersOpen || areFiltersOpenFromFilterPill || areFiltersOpenFromMain) {
      window.document.addEventListener('mousedown', this.handleClickOutside)
    }
  }

  removeHandler () {
    window.document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e: MouseEvent) => {
    if (!this.wrapperRef) return
    if (!this.wrapperRef.contains(e.target as HTMLElement)) {
      this.setState({
        areFiltersOpenFromFilterPill: false,
        areFiltersOpenFromMain: false,
        areFiltersOpen: false
      }, this.removeHandler)
    }
  }

  handleDropdownToggle = () => {
    this.setState(({ areFiltersOpen, areFiltersOpenFromFilterPill, areFiltersOpenFromMain }) => ({
      areFiltersOpen: !areFiltersOpen,
      areFiltersOpenFromFilterPill: (areFiltersOpen && areFiltersOpenFromFilterPill),
      areFiltersOpenFromMain: (areFiltersOpen && areFiltersOpenFromMain),
      selectedFilter: undefined
    }), this.addHandler)
  };

  toggleAppliedFiltersVisibility = () => {
    this.setState(({ areAppliedFiltersVisible }) => ({ areAppliedFiltersVisible: !areAppliedFiltersVisible }))
  }

  onLoad = (filterId: string, items: any[]) => {
    this.setState(({ filters }) => {
      let filterIndex = filters.findIndex(filter => filter.id === filterId)
      const updatedFilter = { ...filters[filterIndex], items};

      return {
        filters: [
          ...filters.slice(0, filterIndex),
          updatedFilter,
          ...filters.slice(filterIndex + 1),
        ]
      }
    })
  }

  getDisplayAppliedFilters () {
    function findByValue (value: string, array: any[], key: string, filterId?: string) {
      for (const node of array) {
        if(!filterId && String(node.value) === String(value)) return node
        if(node.id === filterId) {
          if (String(node.value) === String(value)) return node
          if (node[key]) {
            const child: any = findByValue(value, node[key], key)
            if (child) return child
          }
        }
      }
    }

    const { filters } = this.state
    const { appliedFilters } = this.props

    if(!appliedFilters) return []

    return Object.keys(appliedFilters)
      .filter((key: string) => key !== 'search')
      .reduce((acc: any[], key: string) => {
          if (Array.isArray(appliedFilters[key])) {
            if(appliedFilters[key].length > 0) {
              acc.push(key);
            }
          } else if (appliedFilters[key] !== null) {
            acc.push(key);
          }

          return acc;
        }, [])// not empty arrays
        .map((key: string) => {
        const values = appliedFilters[key]
        const filter = filters.filter(filter => filter.id === key)[0]

        let appliedFilterValues: string[] = []
        let condition = undefined

        switch (filter.type) {
          case 'dynamic':
            appliedFilterValues = values.map((value: { name: string }) => value.name)
            break
          case 'radio':
            const radioFilterOption = findByValue(values, filters, 'items', filter.id)
            appliedFilterValues = [radioFilterOption.name]
            break
          case 'radio-input':
            const radioInput = values
            const radioInputFilterOption = findByValue(radioInput.condition, filters, 'items', filter.id)

            condition = radioInputFilterOption.name
            appliedFilterValues = [radioInput.data]
            break
          case 'date':
            const dateFilter = values
            const dateFilterOption = findByValue(dateFilter.condition, filters, 'items', filter.id)
            condition = dateFilterOption.name
            appliedFilterValues = [
                dateFilter.type === 'absolute' ?
                    formatDateInLocalTimezone(new Date(dateFilter.data).getTime()/1000|0) :
                    `${dateFilter.data} ${dateFilterOption.unit}${dateFilter.data > 1 ? 's' : ''} ago`
            ]
            break
          default:
            appliedFilterValues = Array.isArray(values) ? values.reduce((acc: any[], value: string) => {
              const foundFilter = findByValue(value, filters, 'items', filter.id)
              if(foundFilter) {
                acc.push(foundFilter.name)
              }
              return acc
            }, []) : []
        }

        return {
          filter: key,
          type: filter.type,
          name: filter.name,
          condition,
          values: appliedFilterValues
        }
      })
  }

  onApplyFilters = (id: string, data: any) => {
    this.props.onFilterChange(id, data)
    this.onCancelFilters()
  }

  onCancelFilters = () => {
    this.setState({
      areFiltersOpen: false,
      areFiltersOpenFromFilterPill: false,
      areFiltersOpenFromMain: false,
      selectedFilter: undefined
    })
  }

  onClearFilterCriteria = () => {
    this.props.onClearFilterCriteria()
  }

  getFiltersMenu () {
    const { areFiltersOpenFromFilterPill, selectedFilter } = this.state
    const { appliedFilters, filters } = this.props

    const menuProps = {
      areFiltersOpenFromFilterPill,
      selectedFilter,
      appliedFilters,
      filters,
      onFilterClick: (filterId: string) => this.setState({ selectedFilter: filterId }),
      onFilterBack: () => this.setState({ selectedFilter: undefined }),
      onApplyFilters: this.onApplyFilters,
      onCancelFilters: this.onCancelFilters,
      onLoad: this.onLoad
    }

    return <FiltersMenu {...menuProps} />
  }

  render ({ filters,appliedFilters, onFilterChange, onClearFilterCriteria, withSearch, searchPlaceholder }: Props, { areAppliedFiltersVisible, selectedFilter, areFiltersOpen, areFiltersOpenFromFilterPill, areFiltersOpenFromMain }: State) {
    const displayAppliedFilters = this.getDisplayAppliedFilters()
    const areFiltersApplied = displayAppliedFilters.length > 0
    const mainFilters = filters.filter((filter: any) => filter.isMain)

    return (
      <div ref={node => this.wrapperRef = node}>
        <div class='overview__filters-container'>
            <div>
            {mainFilters.map(mainFilter => <ButtonControl
                newStyle
                title={mainFilter.name}
                size='medium'
                class='overview__filters-container-open-button qa-overview-open-filters'
                onClick={() => this.setState({
                  areFiltersOpen:  false,
                  areFiltersOpenFromFilterPill: false,
                  areFiltersOpenFromMain: !areFiltersOpenFromMain,
                  selectedFilter: mainFilter.id
                }, this.addHandler)}
            />)}
              { areFiltersOpenFromMain && <div class='overview__filters-box-container'>
                <div className='overview__filters-box'>{ this.getFiltersMenu() }</div>
              </div> }
            </div>
            <div>
              <ButtonControl
                newStyle
                title={<div>
                  <Icon icon='filter' height={12} width={12} />
                  Filters
                  <Icon icon='chevronDown' height={16} width={16} />
                </div>}
                size='medium'
                class='overview__filters-container-open-button qa-overview-open-filters'
                onClick={this.handleDropdownToggle}
              />
              { areFiltersOpen && <div class='overview__filters-box-container'>
                <div className='overview__filters-box'>{ this.getFiltersMenu() }</div>
              </div> }
            </div>
            {withSearch && <SearchBox
              placeholder={searchPlaceholder}
              value={appliedFilters && appliedFilters['search']}
              onSubmit={(value: string) => onFilterChange('search', value)}
              onClear={() => onFilterChange('search', '')}
              isModerationOverview
            />}
            { !areAppliedFiltersVisible && <ButtonControl
              newStyle
              outlined
              size='medium'
              title={`${displayAppliedFilters && displayAppliedFilters.length} Hidden filter${displayAppliedFilters.length > 1 ? 's' : ''}`}
              variant='brand'
              class='qa-overview-hide-filters'
              onClick={this.toggleAppliedFiltersVisibility}
            />}
        </div>
        {
          areAppliedFiltersVisible && areFiltersApplied &&
          <PillContainer>
            { displayAppliedFilters.map(({ filter, type, name, condition, values }) => {
              const numberOfAppliedFiltersMinusOne = values && values.length > 1 && values.length - 1
              return <div>
                <Pill newStyle>
                  <div class='truncated' onClick={() => this.setState({
                    areFiltersOpen: false,
                    areFiltersOpenFromFilterPill: !areFiltersOpenFromFilterPill,
                    areFiltersOpenFromMain: false,
                    selectedFilter: filter
                  }, this.addHandler)}>
                    <span class='filter-type'>{name}</span>
                    <span class='filter-value'>
                      { condition && `${condition} ` }
                      {values && values[0]}
                    </span>
                  </div>
                  { numberOfAppliedFiltersMinusOne && <span class='filter-number'>+{numberOfAppliedFiltersMinusOne}</span> }
                  <Icon customClass={`filter-pill-dismiss qa-dismiss-${filter}-filter-pill`} icon='close' width={16}
                    height={16}
                    onClick={() => onFilterChange(filter, type === 'radio' ? null : [])} />
                </Pill>
                {areFiltersOpenFromFilterPill && selectedFilter === filter && <div class='overview__filters-box-container'>
                  <div class='overview__filters-box'>{ this.getFiltersMenu() }</div>
                </div>}
              </div>
            }
            )
            }
            <ButtonControl
              newStyle
              outlined
              size='medium'
              class='qa-overview-clear-filters'
              title='Clear all'
              variant='danger'
              onClick={this.onClearFilterCriteria}
            />
            <ButtonControl
              newStyle
              outlined
              size='medium'
              class='qa-overview-hide-filters'
              title='Hide filters'
              variant='secondary-reversed'
              onClick={this.toggleAppliedFiltersVisibility}
            />
          </PillContainer>
        }
      </div>
    )
  }
}

export default OverviewFilters
